import { Check, ChevronDown } from 'lucide-react'
import { type CSSProperties, type KeyboardEvent, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

export type SelectOption = { value: string; label: string; group?: string; hint?: string; disabled?: boolean }

/**
 * A single-choice dropdown that draws its own menu. The native <select> popup is drawn by the
 * operating system (a dark, rounded, blurred menu on macOS) and cannot be styled; this one
 * matches the app and keeps what the native control gives for free: arrow keys, Home/End,
 * Enter, Escape, type-to-jump, and listbox semantics for screen readers.
 *
 * variant: "input" is a form field; "bare" sits inside a .picker chip; "title" is a page-title
 * picker (the view or cycle name).
 */
type Common = {
  options: readonly SelectOption[]
  placeholder?: string
  variant?: 'input' | 'bare' | 'title'
  className?: string
  style?: CSSProperties
  disabled?: boolean
  id?: string
  'aria-label'?: string
}

export function Select({ value, onChange, ...rest }: Common & { value: string | null; onChange: (value: string) => void }) {
  return (
    <Dropdown
      {...rest}
      selected={v => v === value}
      display={rest.options.find(o => o.value === value)?.label ?? null}
      initial={value}
      onPick={v => v !== value && onChange(v)}
      closeOnPick
    />
  )
}

/** The same dropdown for several choices: options toggle with a checkbox and the menu stays open. */
export function MultiSelect({
  value,
  onChange,
  noun = 'selected',
  ...rest
}: Common & { value: readonly string[]; onChange: (value: string[]) => void; noun?: string }) {
  const labels = rest.options.filter(o => value.includes(o.value)).map(o => o.label)
  return (
    <Dropdown
      {...rest}
      multiple
      selected={v => value.includes(v)}
      display={labels.length === 0 ? null : labels.length <= 2 ? labels.join(', ') : `${labels.length} ${noun}`}
      initial={value[0] ?? null}
      onPick={v => onChange(value.includes(v) ? value.filter(x => x !== v) : [...value, v])}
      closeOnPick={false}
    />
  )
}

function Dropdown({
  options,
  placeholder = 'Choose…',
  variant = 'input',
  className = '',
  style,
  disabled = false,
  id,
  'aria-label': ariaLabel,
  selected,
  display,
  initial,
  onPick,
  closeOnPick,
  multiple = false,
}: Common & {
  selected: (v: string) => boolean
  display: string | null
  initial: string | null
  onPick: (v: string) => void
  closeOnPick: boolean
  multiple?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  const [pos, setPos] = useState<{ left: number; top: number; minWidth: number; maxHeight: number; up: boolean } | null>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const menu = useRef<HTMLDivElement>(null)
  const typed = useRef({ text: '', at: 0 })
  const listId = useId()
  const enabled = options.map((o, i) => (o.disabled ? -1 : i)).filter(i => i >= 0)

  const place = () => {
    const r = trigger.current?.getBoundingClientRect()
    if (!r) return
    const below = window.innerHeight - r.bottom - 8
    const above = r.top - 8
    const up = below < 220 && above > below
    setPos({
      left: Math.min(r.left, window.innerWidth - Math.max(r.width, 180) - 8),
      top: up ? r.top - 4 : r.bottom + 4,
      minWidth: r.width,
      maxHeight: Math.min(360, Math.max(120, up ? above : below)),
      up,
    })
  }
  const show = () => {
    if (disabled) return
    place()
    setActive(
      Math.max(
        0,
        options.findIndex(o => o.value === initial),
      ),
    )
    setOpen(true)
  }
  const close = (refocus = true) => {
    setOpen(false)
    if (refocus) trigger.current?.focus()
  }
  const pick = (i: number) => {
    const o = options[i]
    if (!o || o.disabled) return
    onPick(o.value)
    if (closeOnPick) close()
  }

  // the menu follows the trigger; a click anywhere else closes it
  // biome-ignore lint/correctness/useExhaustiveDependencies: place and close only touch refs and state setters, so they are stable in effect
  useLayoutEffect(() => {
    if (!open) return
    place()
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (!menu.current?.contains(t) && !trigger.current?.contains(t)) close(false)
    }
    const onMove = (e: Event) => {
      if (e.target instanceof Node && menu.current?.contains(e.target)) return
      place()
    }
    document.addEventListener('mousedown', onDown)
    window.addEventListener('scroll', onMove, true)
    window.addEventListener('resize', onMove)
    return () => {
      document.removeEventListener('mousedown', onDown)
      window.removeEventListener('scroll', onMove, true)
      window.removeEventListener('resize', onMove)
    }
  }, [open])

  useEffect(() => {
    if (open && active >= 0) menu.current?.querySelector(`[data-i="${active}"]`)?.scrollIntoView({ block: 'nearest' })
  }, [open, active])

  const step = (dir: 1 | -1) => {
    const at = enabled.indexOf(active)
    const next = at < 0 ? (dir > 0 ? enabled[0] : enabled.at(-1)) : enabled[Math.min(enabled.length - 1, Math.max(0, at + dir))]
    if (next !== undefined) setActive(next)
  }
  const onKey = (e: KeyboardEvent) => {
    if (disabled) return
    const k = e.key
    if (!open) {
      if (k === 'ArrowDown' || k === 'ArrowUp' || k === 'Enter' || k === ' ') {
        e.preventDefault()
        show()
      }
      return
    }
    if (k === 'Escape' || k === 'Tab') {
      if (k === 'Escape') e.preventDefault()
      close(k === 'Escape')
    } else if (k === 'ArrowDown' || k === 'ArrowUp') {
      e.preventDefault()
      step(k === 'ArrowDown' ? 1 : -1)
    } else if (k === 'Home' || k === 'End') {
      e.preventDefault()
      const i = k === 'Home' ? enabled[0] : enabled.at(-1)
      if (i !== undefined) setActive(i)
    } else if (k === 'Enter' || k === ' ') {
      e.preventDefault()
      pick(active)
    } else if (k.length === 1 && /\S/.test(k)) {
      // type to jump: keys typed within a second build up a prefix
      const now = Date.now()
      typed.current = { text: now - typed.current.at < 1000 ? typed.current.text + k.toLowerCase() : k.toLowerCase(), at: now }
      const hit = enabled.find(i => options[i]?.label.toLowerCase().startsWith(typed.current.text))
      if (hit !== undefined) setActive(hit)
    }
  }

  let lastGroup: string | undefined
  return (
    <>
      <button
        ref={trigger}
        id={id}
        type="button"
        role="combobox"
        className={`sel sel-${variant} ${open ? 'open' : ''} ${className}`}
        style={style}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open && active >= 0 ? `${listId}-${active}` : undefined}
        onClick={() => (open ? close() : show())}
        onKeyDown={onKey}
      >
        <span className={`sel-value ${display ? '' : 'placeholder'}`}>{display ?? placeholder}</span>
        <ChevronDown size={variant === 'title' ? 16 : 13} className="sel-chevron" />
      </button>
      {open &&
        pos &&
        createPortal(
          <div
            ref={menu}
            id={listId}
            role="listbox"
            aria-label={ariaLabel}
            aria-multiselectable={multiple || undefined}
            className="sel-menu"
            style={{
              left: pos.left,
              top: pos.top,
              minWidth: pos.minWidth,
              maxHeight: pos.maxHeight,
              transform: pos.up ? 'translateY(-100%)' : undefined,
            }}
          >
            {options.map((o, i) => {
              const header = o.group && o.group !== lastGroup ? o.group : null
              lastGroup = o.group
              return (
                <div key={`${o.group ?? ''}:${o.value}`}>
                  {header && <div className="sel-group">{header}</div>}
                  <div
                    id={`${listId}-${i}`}
                    data-i={i}
                    role="option"
                    tabIndex={-1}
                    aria-selected={selected(o.value)}
                    aria-disabled={o.disabled || undefined}
                    className={`sel-option ${i === active ? 'active' : ''} ${selected(o.value) ? 'selected' : ''} ${o.disabled ? 'disabled' : ''} ${o.group ? 'grouped' : ''}`}
                    onMouseEnter={() => !o.disabled && setActive(i)}
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => pick(i)}
                    onKeyDown={onKey}
                  >
                    <span className={`sel-check ${multiple ? 'box' : ''}`}>{selected(o.value) && <Check size={multiple ? 10 : 12} />}</span>
                    <span className="sel-label">{o.label}</span>
                    {o.hint && <span className="sel-hint">{o.hint}</span>}
                  </div>
                </div>
              )
            })}
            {options.length === 0 && <div className="sel-empty">Nothing to choose</div>}
          </div>,
          document.body,
        )}
    </>
  )
}
